/**
 * KAN-875: горизонтальный контейнер со скрытой полосой прокрутки прокручивается
 * мышью (доска менеджера, открытая в браузере компьютера).
 * - Колесо: вертикальный deltaY (без deltaX) сдвигает контейнер по горизонтали.
 *   preventDefault только когда контейнер реально может сдвинуться в эту
 *   сторону: на краю страница прокручивается как обычно. Горизонтальный жест
 *   тачпада и ctrl+колесо (масштаб) отдаются браузеру. wheelPaging (карусель со
 *   scroll-snap): один жест колеса листает на страницу, иначе snap возвращал бы
 *   мелкий сдвиг обратно.
 *   Над вложенным вертикально прокручиваемым элементом (список карточек колонки)
 *   колесо не перехватывается вовсе, даже на краю списка: крутится список, а на
 *   его краю страница. Карусель колесом листается над шапкой и пустым местом,
 *   иначе стрелками; так прокрутка списка не перескакивает в соседнюю колонку.
 * - Перетаскивание: только pointerType "mouse" (касание прокручивает браузер),
 *   порог DRAG_THRESHOLD_PX; завершается по pointerup/pointercancel, потере
 *   захвата и движению без нажатой кнопки (отпустили вне окна); на время перетаскивания snap и плавная прокрутка
 *   выключены, после перетаскивания следующий click гасится (не открыть
 *   вкладку/карточку).
 * - canScrollLeft/canScrollRight: по scroll, ResizeObserver и смене детей.
 * Узел приходит через callback ref (контейнер может появиться позже хука,
 * например после загрузки); externalRef получает тот же узел.
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

export type ScrollDirection = 'left' | 'right';

/** Сдвиг мыши, после которого нажатие считается перетаскиванием. */
export const DRAG_THRESHOLD_PX = 5;
/** Доля ширины контейнера для сдвига стрелкой и листания колесом. */
export const PAGE_FRACTION = 0.8;
/** Пауза листания колесом: серия событий одного жеста листает один раз. */
export const WHEEL_PAGE_COOLDOWN_MS = 450;

const LINE_HEIGHT_PX = 16;
/** Допуск на дробные пиксели scrollLeft при масштабе страницы. */
const EDGE_EPSILON = 1;

export interface UseHorizontalScrollOptions<T extends HTMLElement> {
  /** Ref, которому тоже нужен узел контейнера (например, скроллер вкладок доски). */
  externalRef?: MutableRefObject<T | null>;
  /** Колесо листает на страницу (для scroll-snap карусели). */
  wheelPaging?: boolean;
}

export interface HorizontalScroll<T extends HTMLElement> {
  ref: (node: T | null) => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollPage: (direction: ScrollDirection) => void;
}

function maxScroll(el: HTMLElement): number {
  return Math.max(0, el.scrollWidth - el.clientWidth);
}

function canMove(el: HTMLElement, delta: number): boolean {
  if (delta > 0) return el.scrollLeft < maxScroll(el) - EDGE_EPSILON;
  if (delta < 0) return el.scrollLeft > EDGE_EPSILON;
  return false;
}

function wheelDelta(event: WheelEvent, el: HTMLElement): number {
  if (event.deltaMode === 1) return event.deltaY * LINE_HEIGHT_PX;
  if (event.deltaMode === 2) return event.deltaY * el.clientWidth;
  return event.deltaY;
}

/** Между target и контейнером есть вертикально прокручиваемый элемент с переполнением. */
function insideVerticalScroller(target: EventTarget | null, container: HTMLElement): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== container) {
    if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight + EDGE_EPSILON) {
      const { overflowY } = getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') return true;
    }
    node = node.parentElement;
  }
  return false;
}

function smoothScrollBy(el: HTMLElement, left: number): void {
  if (typeof el.scrollBy === 'function') {
    el.scrollBy({ left, behavior: 'smooth' });
  } else {
    el.scrollLeft += left;
  }
}

function pageOffset(el: HTMLElement, direction: ScrollDirection): number {
  const step = Math.round(el.clientWidth * PAGE_FRACTION);
  return direction === 'right' ? step : -step;
}

/** Все обработчики мыши на узле; возвращает очистку. */
function bindMouseScroll(el: HTMLElement, wheelPaging: boolean, update: () => void): () => void {
  let wheelLockedUntil = 0;
  let drag: { startX: number; startLeft: number; pointerId: number; moved: boolean } | null = null;
  let suppressClick = false;
  let suppressTimer: ReturnType<typeof setTimeout> | null = null;
  let savedStyle: { snap: string; behavior: string; userSelect: string } | null = null;

  const onWheel = (event: WheelEvent) => {
    if (event.ctrlKey || event.deltaX !== 0 || event.deltaY === 0) return;
    if (insideVerticalScroller(event.target, el)) return;
    const delta = wheelDelta(event, el);
    if (!canMove(el, delta)) return;
    event.preventDefault();
    if (!wheelPaging) {
      el.scrollLeft += delta;
      return;
    }
    const now = Date.now();
    if (now < wheelLockedUntil) return;
    wheelLockedUntil = now + WHEEL_PAGE_COOLDOWN_MS;
    smoothScrollBy(el, pageOffset(el, delta > 0 ? 'right' : 'left'));
  };

  const startDragStyle = () => {
    savedStyle = {
      snap: el.style.scrollSnapType,
      behavior: el.style.scrollBehavior,
      userSelect: el.style.userSelect,
    };
    el.style.scrollSnapType = 'none';
    el.style.scrollBehavior = 'auto';
    el.style.userSelect = 'none';
  };

  const restoreDragStyle = () => {
    if (!savedStyle) return;
    el.style.scrollSnapType = savedStyle.snap;
    el.style.scrollBehavior = savedStyle.behavior;
    el.style.userSelect = savedStyle.userSelect;
    savedStyle = null;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    suppressClick = false;
    drag = { startX: event.clientX, startLeft: el.scrollLeft, pointerId: event.pointerId, moved: false };
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag || event.pointerType !== 'mouse') return;
    if (event.buttons === 0) {
      endDrag();
      return;
    }
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      startDragStyle();
      window.getSelection?.()?.removeAllRanges();
      try {
        el.setPointerCapture?.(drag.pointerId);
      } catch {
        // указатель уже отпущен: захват не нужен
      }
    }
    el.scrollLeft = drag.startLeft - dx;
  };

  const endDrag = () => {
    if (!drag) return;
    const { moved, pointerId } = drag;
    drag = null;
    if (!moved) return;
    restoreDragStyle();
    try {
      if (el.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId);
    } catch {
      // захвата нет
    }
    suppressClick = true;
    if (suppressTimer) clearTimeout(suppressTimer);
    // click приходит сразу после pointerup; если отпустили вне контейнера, флаг снимет таймер
    suppressTimer = setTimeout(() => {
      suppressClick = false;
      suppressTimer = null;
    }, 0);
  };

  const onClickCapture = (event: MouseEvent) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // Нативное перетаскивание ссылки/картинки сорвало бы прокрутку мышью.
  const onDragStart = (event: DragEvent) => {
    if (drag) event.preventDefault();
  };

  el.addEventListener('wheel', onWheel, { passive: false });
  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', endDrag);
  el.addEventListener('pointercancel', endDrag);
  el.addEventListener('lostpointercapture', endDrag);
  el.addEventListener('click', onClickCapture, true);
  el.addEventListener('dragstart', onDragStart);
  el.addEventListener('scroll', update, { passive: true });

  return () => {
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', endDrag);
    el.removeEventListener('pointercancel', endDrag);
    el.removeEventListener('lostpointercapture', endDrag);
    el.removeEventListener('click', onClickCapture, true);
    el.removeEventListener('dragstart', onDragStart);
    el.removeEventListener('scroll', update);
    if (suppressTimer) clearTimeout(suppressTimer);
    restoreDragStyle();
  };
}

/** Следит за размерами контейнера и его детей; возвращает очистку. */
function observeSize(el: HTMLElement, update: () => void): () => void {
  const cleanups: Array<() => void> = [];
  if (typeof ResizeObserver !== 'undefined') {
    const resize = new ResizeObserver(update);
    const observed = new Set<Element>();
    const syncChildren = () => {
      const current = new Set(Array.from(el.children));
      observed.forEach((child) => {
        if (current.has(child)) return;
        resize.unobserve(child);
        observed.delete(child);
      });
      current.forEach((child) => {
        if (observed.has(child)) return;
        resize.observe(child);
        observed.add(child);
      });
    };
    resize.observe(el);
    syncChildren();
    cleanups.push(() => {
      resize.disconnect();
      observed.clear();
    });
    if (typeof MutationObserver !== 'undefined') {
      const mutation = new MutationObserver(() => {
        syncChildren();
        update();
      });
      mutation.observe(el, { childList: true });
      cleanups.push(() => mutation.disconnect());
    }
  }
  return () => cleanups.forEach((cleanup) => cleanup());
}

export function useHorizontalScroll<T extends HTMLElement>(
  options: UseHorizontalScrollOptions<T> = {}
): HorizontalScroll<T> {
  const { externalRef, wheelPaging = false } = options;
  const [node, setNode] = useState<T | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const externalRefRef = useRef(externalRef);
  externalRefRef.current = externalRef;

  const ref = useCallback((next: T | null) => {
    const external = externalRefRef.current;
    if (external) external.current = next;
    setNode(next);
  }, []);

  useEffect(() => {
    if (!node) return undefined;
    const update = () => {
      const left = canMove(node, -1);
      const right = canMove(node, 1);
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    update();
    const unbindMouse = bindMouseScroll(node, wheelPaging, update);
    const unobserve = observeSize(node, update);
    return () => {
      unbindMouse();
      unobserve();
    };
  }, [node, wheelPaging]);

  const scrollPage = useCallback(
    (direction: ScrollDirection) => {
      if (node) smoothScrollBy(node, pageOffset(node, direction));
    },
    [node]
  );

  return { ref, canScrollLeft: edges.left, canScrollRight: edges.right, scrollPage };
}
